import { toast } from "react-hot-toast";
import { Building2, Info, Image as ImageIcon, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import {
  businessTypeOptions,
  timezoneOptions,
  currencyOptions,
  type BusinessProfile,
  type BusinessType,
  type CurrencyCode,
} from "../mockSettings";

interface BusinessProfileSectionProps {
  profile: BusinessProfile;
  onChange: (patch: Partial<BusinessProfile>) => void;
}

export function BusinessProfileSection({ profile, onChange }: BusinessProfileSectionProps) {
  const handleSave = () => {
    toast.success("Business profile saved");
  };

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Business Profile &amp; Brand Persona
          </span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Informs AI assistant tone</span>
      </div>

      <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-4">
        <div className="flex items-start gap-2 rounded-xl border border-sky-200/80 dark:border-sky-900/40 bg-sky-50/60 dark:bg-sky-950/30 p-2.5 text-xs text-sky-800 dark:text-sky-300">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden="true" />
          <p className="leading-snug">
            This business identity is injected into your AI assistant prompt to introduce your brand and speak accurately on your behalf.
          </p>
        </div>

        <div className="flex items-center gap-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs">
          {profile.logoUrl ? (
            <img src={profile.logoUrl} alt="Business logo" className="h-12 w-12 rounded-xl object-cover shadow-xs border border-slate-200/80 dark:border-slate-700" />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary shadow-xs">
              {profile.logoInitials}
            </div>
          )}
          <div>
            <Button type="button" variant="outline" className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 gap-1.5" onClick={() => toast("Logo upload coming soon")}>
              <ImageIcon className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
              Change logo
            </Button>
            <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500 font-medium">Square PNG or SVG recommended.</p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="business-name" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Business name</Label>
            <Input
              id="business-name"
              value={profile.businessName}
              onChange={(e) => onChange({ businessName: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="business-type" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Business type</Label>
            <Select
              id="business-type"
              value={profile.businessType}
              onChange={(e) => onChange({ businessType: e.target.value as BusinessType })}
              className="mt-1"
            >
              {businessTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="business-description" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Short Business Description</Label>
          <Textarea
            id="business-description"
            rows={2.5}
            value={profile.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className="mt-1 text-xs"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="business-timezone" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Timezone</Label>
            <Select
              id="business-timezone"
              value={profile.timezone}
              onChange={(e) => onChange({ timezone: e.target.value })}
              className="mt-1"
            >
              {timezoneOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="business-currency" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Default currency</Label>
            <Select
              id="business-currency"
              value={profile.currency}
              onChange={(e) => onChange({ currency: e.target.value as CurrencyCode })}
              className="mt-1"
            >
              {currencyOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="support-email" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Support email</Label>
            <Input
              id="support-email"
              type="email"
              value={profile.supportEmail}
              onChange={(e) => onChange({ supportEmail: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <Button className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs gap-1.5" onClick={handleSave}>
            <Check className="h-4 w-4" />
            Save changes
          </Button>
        </div>
      </div>
    </Card>
  );
}
